// src/utils/energeticCorrelations.ts
// Energetic & Spiritual layer correlation utilities (Sanctuary tier)

import { supabase } from '@/integrations/supabase/client';
import { subDays, format } from 'date-fns';

export interface EnergeticCorrelationResult {
  correlation_type: string;
  metric_a: string;
  metric_b: string;
  avg_metric_a_low: number;
  avg_metric_a_high: number;
  avg_metric_b_low: number;
  avg_metric_b_high: number;
  interpretation: string;
  sample_size: number;
}

// Helper to calculate median
function median(values: number[]): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

// Helper to calculate average
function average(values: number[]): number {
  if (values.length === 0) return 0;
  return values.reduce((a, b) => a + b, 0) / values.length;
}

// Map daily_feeling to numeric score
const feelingToScore: Record<string, number> = {
  'amazing': 5,
  'good': 4,
  'okay': 3,
  'struggling': 2,
  'crisis': 1
};

/**
 * Correlate Vibrational Frequency with Mood (from daily_checkins)
 */
export async function getVibrationalFrequencyMoodCorrelation(
  userId: string,
  days: number = 60
): Promise<EnergeticCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch vibrational frequency logs
  const { data: freqLogs, error: freqError } = await supabase
    .from('vibrational_frequency_log')
    .select('frequency_level, emotional_state, created_at')
    .eq('user_id', userId)
    .gte('created_at', `${startDate}T00:00:00.000Z`);

  if (freqError || !freqLogs?.length) return null;

  // Aggregate by date
  const dataByDate = new Map<string, { frequency: number[]; mood: string[] }>();
  
  for (const log of freqLogs) {
    const date = log.created_at.split('T')[0];
    if (!dataByDate.has(date)) {
      dataByDate.set(date, { frequency: [], mood: [] });
    }
    const entry = dataByDate.get(date)!;
    if (log.frequency_level !== null) entry.frequency.push(log.frequency_level);
    if (log.emotional_state) entry.mood.push(log.emotional_state);
  }

  const matchedDays: { frequency: number; mood: number }[] = [];
  
  for (const [_, data] of dataByDate.entries()) {
    if (data.frequency.length > 0 && data.mood.length > 0) {
      const avgFreq = average(data.frequency);
      // Map moods to numeric scores
      const moodScores = data.mood
        .map(m => feelingToScore[m.toLowerCase()] || 3)
        .filter(s => s > 0);
      if (moodScores.length > 0) {
        matchedDays.push({
          frequency: avgFreq,
          mood: average(moodScores)
        });
      }
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by median frequency
  const freqMedian = median(matchedDays.map(d => d.frequency));
  const lowFreq = matchedDays.filter(d => d.frequency < freqMedian);
  const highFreq = matchedDays.filter(d => d.frequency >= freqMedian);

  if (lowFreq.length < 2 || highFreq.length < 2) return null;

  const avgMoodLowFreq = average(lowFreq.map(d => d.mood));
  const avgMoodHighFreq = average(highFreq.map(d => d.mood));
  const diff = avgMoodHighFreq - avgMoodLowFreq;

  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `When your vibrational frequency is higher (avg ${average(highFreq.map(d => d.frequency)).toFixed(0)} Hz), your mood tends to be noticeably better (${avgMoodHighFreq.toFixed(1)} vs ${avgMoodLowFreq.toFixed(1)}). This suggests your energetic state and emotional wellbeing may be connected.`;
  } else if (diff > 0.2) {
    interpretation = `There's a gentle link between higher vibrational frequency and improved mood. Your energy and emotions appear to move together.`;
  } else {
    interpretation = `Your mood appears relatively stable across different vibrational frequencies. This stability can be a strength.`;
  }

  return {
    correlation_type: 'vibrational_frequency_mood',
    metric_a: 'Vibrational Frequency',
    metric_b: 'Mood',
    avg_metric_a_low: average(lowFreq.map(d => d.frequency)),
    avg_metric_a_high: average(highFreq.map(d => d.frequency)),
    avg_metric_b_low: avgMoodLowFreq,
    avg_metric_b_high: avgMoodHighFreq,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Chakra Healing Sessions with Energy Levels
 */
export async function getChakraHealingEnergyCorrelation(
  userId: string,
  days: number = 60
): Promise<EnergeticCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch chakra healing sessions
  const { data: chakraSessions, error: chakraError } = await supabase
    .from('chakra_healing_sessions')
    .select('energy_before, energy_after, created_at')
    .eq('user_id', userId)
    .gte('created_at', `${startDate}T00:00:00.000Z`);

  if (chakraError || !chakraSessions?.length) return null;

  // Calculate energy improvement per session
  const improvements: { before: number; after: number; improvement: number }[] = [];
  
  for (const session of chakraSessions) {
    if (session.energy_before !== null && session.energy_after !== null) {
      improvements.push({
        before: session.energy_before,
        after: session.energy_after,
        improvement: session.energy_after - session.energy_before
      });
    }
  }

  if (improvements.length < 3) return null;

  const avgBefore = average(improvements.map(i => i.before));
  const avgAfter = average(improvements.map(i => i.after));
  const avgImprovement = average(improvements.map(i => i.improvement));

  let interpretation = '';
  if (avgImprovement > 1.5) {
    interpretation = `Your chakra healing sessions show meaningful energy shifts (avg ${avgBefore.toFixed(1)} → ${avgAfter.toFixed(1)}). This practice appears to support your energetic wellbeing.`;
  } else if (avgImprovement > 0.5) {
    interpretation = `There's a gentle lift in energy after chakra work (avg improvement of ${avgImprovement.toFixed(1)} points). Consistent practice may deepen these shifts.`;
  } else if (avgImprovement > 0) {
    interpretation = `Chakra sessions show subtle energy movement. You may notice more profound shifts over time as your practice develops.`;
  } else {
    interpretation = `Energy levels remain stable through chakra sessions. This consistency can reflect groundedness in your practice.`;
  }

  return {
    correlation_type: 'chakra_healing_energy',
    metric_a: 'Chakra Healing Sessions',
    metric_b: 'Energy Level',
    avg_metric_a_low: avgBefore,
    avg_metric_a_high: avgAfter,
    avg_metric_b_low: avgBefore,
    avg_metric_b_high: avgAfter,
    interpretation,
    sample_size: improvements.length
  };
}

/**
 * Correlate Sitting in the Power with Spiritual Connection (from daily_checkins)
 */
export async function getSittingInPowerSpiritualCorrelation(
  userId: string,
  days: number = 60
): Promise<EnergeticCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch sitting in power sessions
  const { data: sipSessions, error: sipError } = await supabase
    .from('sitting_in_power_sessions')
    .select('duration_minutes, created_at')
    .eq('user_id', userId)
    .gte('created_at', `${startDate}T00:00:00.000Z`);

  if (sipError) return null;

  // Aggregate SIP minutes by date
  const sipByDate = new Map<string, number>();
  sipSessions?.forEach(s => {
    const date = s.created_at.split('T')[0];
    sipByDate.set(date, (sipByDate.get(date) || 0) + (s.duration_minutes || 0));
  });

  // Fetch daily checkins with spiritual_connection
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, spiritual_connection')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (checkinError || !checkins?.length) return null;

  // Map spiritual_connection to numeric
  const spiritualToScore: Record<string, number> = {
    'deeply_connected': 5,
    'connected': 4,
    'somewhat_connected': 3,
    'disconnected': 2,
    'very_disconnected': 1
  };

  const matchedDays: { sipMinutes: number; spiritual: number }[] = [];
  
  for (const c of checkins) {
    if (c.spiritual_connection && spiritualToScore[c.spiritual_connection]) {
      matchedDays.push({
        sipMinutes: sipByDate.get(c.date) || 0,
        spiritual: spiritualToScore[c.spiritual_connection]
      });
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by SIP practice (any vs none)
  const withSIP = matchedDays.filter(d => d.sipMinutes > 0);
  const withoutSIP = matchedDays.filter(d => d.sipMinutes === 0);

  if (withSIP.length < 2 || withoutSIP.length < 2) return null;

  const avgSpiritualWithSIP = average(withSIP.map(d => d.spiritual));
  const avgSpiritualWithoutSIP = average(withoutSIP.map(d => d.spiritual));
  const diff = avgSpiritualWithSIP - avgSpiritualWithoutSIP;

  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `Days with Sitting in the Power practice show stronger spiritual connection (avg ${avgSpiritualWithSIP.toFixed(1)} vs ${avgSpiritualWithoutSIP.toFixed(1)}). This meditation appears to deepen your sense of alignment.`;
  } else if (diff > 0.2) {
    interpretation = `There's a gentle link between Sitting in the Power and spiritual connection. Your practice may be quietly strengthening this bond.`;
  } else {
    interpretation = `Your spiritual connection remains steady regardless of formal practice. This inner stability is a foundation to build upon.`;
  }

  return {
    correlation_type: 'sip_spiritual_connection',
    metric_a: 'Sitting in the Power',
    metric_b: 'Spiritual Connection',
    avg_metric_a_low: 0,
    avg_metric_a_high: average(withSIP.map(d => d.sipMinutes)),
    avg_metric_b_low: avgSpiritualWithoutSIP,
    avg_metric_b_high: avgSpiritualWithSIP,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Energy Body observations with Emotional State
 */
export async function getEnergyBodyEmotionalCorrelation(
  userId: string,
  days: number = 60
): Promise<EnergeticCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch energy body logs
  const { data: energyLogs, error: energyError } = await supabase
    .from('energy_body_logs')
    .select('energy_level, blockages_noted, created_at')
    .eq('user_id', userId)
    .gte('created_at', `${startDate}T00:00:00.000Z`);

  if (energyError || !energyLogs?.length) return null;

  // Aggregate by date
  const energyByDate = new Map<string, { level: number; hasBlockages: boolean }>();
  
  for (const log of energyLogs) {
    const date = log.created_at.split('T')[0];
    if (!energyByDate.has(date)) {
      energyByDate.set(date, { level: log.energy_level || 5, hasBlockages: false });
    }
    const entry = energyByDate.get(date)!;
    if (log.energy_level) entry.level = log.energy_level;
    if (log.blockages_noted && log.blockages_noted.trim().length > 0) entry.hasBlockages = true;
  }

  // Fetch daily checkins for mood
  const { data: checkins, error: checkinError } = await supabase
    .from('daily_checkins')
    .select('date, daily_feeling')
    .eq('user_id', userId)
    .gte('date', startDate);

  if (checkinError || !checkins?.length) return null;

  const matchedDays: { energyLevel: number; mood: number; hasBlockages: boolean }[] = [];
  
  for (const c of checkins) {
    const energy = energyByDate.get(c.date);
    if (energy && c.daily_feeling && feelingToScore[c.daily_feeling]) {
      matchedDays.push({
        energyLevel: energy.level,
        mood: feelingToScore[c.daily_feeling],
        hasBlockages: energy.hasBlockages
      });
    }
  }

  if (matchedDays.length < 5) return null;

  // Split by energy level median
  const energyMedian = median(matchedDays.map(d => d.energyLevel));
  const lowEnergy = matchedDays.filter(d => d.energyLevel < energyMedian);
  const highEnergy = matchedDays.filter(d => d.energyLevel >= energyMedian);

  if (lowEnergy.length < 2 || highEnergy.length < 2) return null;

  const avgMoodLowEnergy = average(lowEnergy.map(d => d.mood));
  const avgMoodHighEnergy = average(highEnergy.map(d => d.mood));
  const diff = avgMoodHighEnergy - avgMoodLowEnergy;

  let interpretation = '';
  if (diff > 0.5) {
    interpretation = `Higher energy body readings correlate with better emotional states (mood ${avgMoodHighEnergy.toFixed(1)} vs ${avgMoodLowEnergy.toFixed(1)}). Your energetic and emotional layers appear interconnected.`;
  } else if (diff > 0.2) {
    interpretation = `There's a gentle link between your energy body observations and emotional wellbeing. As one shifts, the other may follow.`;
  } else {
    interpretation = `Your emotional state remains relatively stable across different energy body readings. This resilience is noteworthy.`;
  }

  return {
    correlation_type: 'energy_body_emotional',
    metric_a: 'Energy Body Level',
    metric_b: 'Emotional State',
    avg_metric_a_low: average(lowEnergy.map(d => d.energyLevel)),
    avg_metric_a_high: average(highEnergy.map(d => d.energyLevel)),
    avg_metric_b_low: avgMoodLowEnergy,
    avg_metric_b_high: avgMoodHighEnergy,
    interpretation,
    sample_size: matchedDays.length
  };
}

/**
 * Correlate Chakra Sessions with Emotional Settling
 */
export async function getChakraEmotionalSettlingCorrelation(
  userId: string,
  days: number = 60
): Promise<EnergeticCorrelationResult | null> {
  const startDate = format(subDays(new Date(), days), 'yyyy-MM-dd');

  // Fetch chakra healing sessions
  const { data: chakraSessions, error: chakraError } = await supabase
    .from('chakra_healing_sessions')
    .select('emotional_state_before, emotional_state_after, created_at')
    .eq('user_id', userId)
    .gte('created_at', `${startDate}T00:00:00.000Z`);

  if (chakraError || !chakraSessions?.length) return null;

  // Map emotional states to numeric
  const emotionalToScore: Record<string, number> = {
    'peaceful': 5,
    'calm': 4,
    'neutral': 3,
    'anxious': 2,
    'distressed': 1,
    'agitated': 1
  };

  const sessions: { before: number; after: number }[] = [];
  
  for (const session of chakraSessions) {
    const beforeScore = session.emotional_state_before ? 
      emotionalToScore[session.emotional_state_before.toLowerCase()] || 3 : null;
    const afterScore = session.emotional_state_after ?
      emotionalToScore[session.emotional_state_after.toLowerCase()] || 3 : null;
    
    if (beforeScore !== null && afterScore !== null) {
      sessions.push({ before: beforeScore, after: afterScore });
    }
  }

  if (sessions.length < 3) return null;

  const avgBefore = average(sessions.map(s => s.before));
  const avgAfter = average(sessions.map(s => s.after));
  const avgImprovement = average(sessions.map(s => s.after - s.before));

  let interpretation = '';
  if (avgImprovement > 0.5) {
    interpretation = `Chakra healing sessions tend to shift your emotional state towards greater calm (${avgBefore.toFixed(1)} → ${avgAfter.toFixed(1)}). This practice supports emotional regulation.`;
  } else if (avgImprovement > 0.2) {
    interpretation = `There's a gentle settling of emotions after chakra work. Your nervous system appears responsive to this practice.`;
  } else {
    interpretation = `Emotional states remain stable through chakra sessions. This groundedness can serve as a foundation for deeper work.`;
  }

  return {
    correlation_type: 'chakra_emotional_settling',
    metric_a: 'Chakra Healing',
    metric_b: 'Emotional Settling',
    avg_metric_a_low: avgBefore,
    avg_metric_a_high: avgAfter,
    avg_metric_b_low: avgBefore,
    avg_metric_b_high: avgAfter,
    interpretation,
    sample_size: sessions.length
  };
}

/**
 * Get all energetic correlations for a user
 */
export async function getAllEnergeticCorrelations(
  userId: string,
  days: number = 60
): Promise<EnergeticCorrelationResult[]> {
  const results: EnergeticCorrelationResult[] = [];

  const correlationFns = [
    getVibrationalFrequencyMoodCorrelation,
    getChakraHealingEnergyCorrelation,
    getSittingInPowerSpiritualCorrelation,
    getEnergyBodyEmotionalCorrelation,
    getChakraEmotionalSettlingCorrelation
  ];

  const correlations = await Promise.all(
    correlationFns.map(fn => fn(userId, days).catch(() => null))
  );

  for (const c of correlations) {
    if (c !== null && c.interpretation) {
      results.push(c);
    }
  }

  return results;
}
